/**
 * 배포 화면 "서비스 · 버전 · 설정" 표의 설정 파일 칩.
 *
 * 현장 열마다 그 방재실(설정 코드)이 읽는 서비스 설정 파일(router.env·lights.toml …)을 버전 아래
 * 칩으로 두고, 누르면 바로 편집 창이 열린다. 열이 방재실을 이미 말해 주니 칩엔 코드를 안 붙인다.
 * 한 서비스 전용이 아닌 파일(사내 compose·영상처리서버)은 표 아래 공통 설정 줄에 둔다 —
 * 거기선 방재실이 여러 곳이면 칩에 설정 코드를 붙인다.
 */
import React from 'react';
import { Alert, Box, Button, Chip, Link, Stack, Tooltip, Typography, alpha } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';

import type { SiteConfigFile, SiteConfigList, SiteConfigSite } from '../../../services/SiteConfigService';
import type { SiteLiveStatus } from '../../../services/SiteHealthService';
import { isSharedConfig, serviceConfigNames } from '../../../views/SiteDashboardView/hooks/deployConfigFiles';
import { useDashboardTokens } from '../../../views/SiteDashboardView/tokens';
import type { SiteConfigEditorTarget } from './SiteConfigEditorDialog';
import { LIVE_CONFIG_TEXT, commitTitle, compareLiveConfig, findLiveConfig } from './siteConfigFiles';

type OpenConfig = (target: SiteConfigEditorTarget) => void;

interface ConfigChipProps {
  site: SiteConfigSite;
  file: SiteConfigFile;
  /** 방재실이 여러 곳일 때만 설정 코드를 붙인다 */
  showCode: boolean;
  writable: boolean;
  onOpen: OpenConfig;
  /** 서비스 행의 칩 — 현장이 이 서비스로 실제 읽는 파일과 견준다. 공통 설정 칩은 없음. */
  app?: string;
  liveSites?: SiteLiveStatus[];
}

const ConfigChip: React.FC<ConfigChipProps> = ({ site, file, showCode, writable, onOpen, app, liveSites }) => {
  const { type } = useDashboardTokens();
  const live = app && liveSites ? findLiveConfig(liveSites, site.room, app, file.name) : undefined;
  const compare = compareLiveConfig(file, live);
  // 다를 때만 글자로 붙인다 — 폰에서는 툴팁이 안 보인다.
  const flagged = compare === 'different' || compare === 'site_missing';
  const liveNote = LIVE_CONFIG_TEXT[compare];
  const title = !writable
    ? '열기·수정은 관리자·편집자만 할 수 있습니다'
    : `${file.label} — ${file.exists ? '눌러서 바로 수정' : '아직 없는 파일, 저장하면 만들어집니다'}${liveNote ? ` · ${liveNote}` : ''}`;
  const name = showCode ? `${file.name} · ${site.code}` : file.name;
  return (
    <Tooltip title={title} arrow>
      <span>
        <Chip
          size="small"
          variant={file.exists ? 'outlined' : 'filled'}
          color={flagged ? 'warning' : 'default'}
          icon={<EditOutlined />}
          label={flagged ? `${name} · ${liveNote}` : name}
          onClick={
            writable
              ? () => onOpen({ code: site.code, name: file.name, label: `${file.label} · ${file.name}`, app, room: site.room, live })
              : undefined
          }
          disabled={!writable}
          data-testid={`config-chip-${site.code}-${file.name}`}
          data-live={compare}
          // 테마가 칩 글자를 대문자로 바꾼다 — 파일 이름은 저장소 그대로 보여야 한다.
          sx={{ maxWidth: '100%', fontFamily: type.mono.family, fontSize: '0.75rem', '& .MuiChip-label': { textTransform: 'none' } }}
        />
      </span>
    </Tooltip>
  );
};

/** 칩으로 그릴 (방재실, 파일) 목록 — 폴더를 못 읽은 방재실은 뺀다. */
function pick(sites: SiteConfigSite[], keep: (f: SiteConfigFile) => boolean) {
  return sites.filter((s) => !s.problem).flatMap((s) => s.files.filter(keep).map((file) => ({ site: s, file })));
}

interface ConfigChipsProps {
  app: string;
  /** 이 현장 열의 설정 폴더 — 없으면(설정 코드 미등록) 아무것도 안 그린다. */
  site?: SiteConfigSite;
  /** 이 현장 통합서버 점검 결과(방재실별) — 칩에 "현장과 다름" 을 붙인다. */
  liveSites: SiteLiveStatus[];
  loading: boolean;
  writable: boolean;
  onOpen: OpenConfig;
}

/** 현장 열 버전 아래 — 이 서비스가 그 방재실에서 읽는 설정 파일 칩. */
export const ConfigChips: React.FC<ConfigChipsProps> = ({ app, site, liveSites, loading, writable, onOpen }) => {
  const { colors } = useDashboardTokens();
  const names = serviceConfigNames(app);
  const chips = site ? pick([site], (f) => names.includes(f.name)) : [];
  if (chips.length === 0) {
    return loading ? <Typography sx={{ color: colors.muted, fontSize: '0.75rem' }}>…</Typography> : null;
  }
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5} sx={{ mt: 0.75 }} data-testid={`config-chips-${site!.code}-${app}`}>
      {chips.map(({ site: s, file }) => (
        <ConfigChip key={file.name} site={s} file={file} showCode={false} writable={writable} onOpen={onOpen} app={app} liveSites={liveSites} />
      ))}
    </Stack>
  );
};

interface ConfigFooterProps {
  data?: SiteConfigList;
  error: unknown;
  writable: boolean;
  onOpen: OpenConfig;
  onRefresh: () => void;
}

/** 표 아래 — 공통 설정 파일, 설정 폴더·마지막 변경, 저장 방식 안내. */
export const ConfigFooter: React.FC<ConfigFooterProps> = ({ data, error, writable, onOpen, onRefresh }) => {
  const { colors, type, space } = useDashboardTokens();
  const sites = data?.sites ?? [];
  const shared = pick(sites, (f) => isSharedConfig(f.name));
  return (
    <Box
      sx={{ borderTop: `1px solid ${colors.border}`, px: `${space.md}px`, py: `${space.sm}px`, bgcolor: alpha(colors.text, 0.025) }}
      data-testid="config-footer"
    >
      <Stack spacing={1}>
        {error ? (
          <Alert severity="error">{error instanceof Error ? error.message : '설정 파일 목록을 읽지 못했습니다'}</Alert>
        ) : null}
        {shared.length > 0 && (
          <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap gap={0.5}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: colors.text, mr: 0.5 }}>공통 설정</Typography>
            {shared.map(({ site, file }) => (
              <ConfigChip
                key={`${site.code}-${file.name}`}
                site={site}
                file={file}
                showCode={sites.length > 1}
                writable={writable}
                onOpen={onOpen}
              />
            ))}
          </Stack>
        )}
        {sites.map((s) =>
          s.problem ? (
            <Alert key={`${s.room}-${s.code}`} severity="warning">
              {s.room ? `${s.room}: ` : ''}
              {s.problem}
            </Alert>
          ) : (
            <Typography key={`${s.room}-${s.code}`} variant="caption" color="text.secondary" component="div">
              <Box component="span" sx={{ fontFamily: type.mono.family, wordBreak: 'break-all' }}>
                {s.path}/
              </Box>
              {s.lastCommit ? (
                <>
                  {' '}· 마지막 변경 {new Date(s.lastCommit.date).toLocaleString('ko-KR')} ·{' '}
                  <Link href={s.lastCommit.html_url} target="_blank" rel="noopener noreferrer">
                    {commitTitle(s.lastCommit.message)}
                  </Link>
                </>
              ) : (
                <span data-testid="site-config-no-folder">
                  {' '}· 저장소에 {s.code} 폴더가 아직 없습니다 — 파일을 처음 저장하면 만들어집니다.
                  {(data?.folders ?? []).length > 0 &&
                    ` 있는 폴더: ${data?.folders.join(', ')} — 이 중 하나라면 현장 설정 > 원격 상태 점검 접속 정보의 설정 코드를 맞춰 주세요.`}
                </span>
              )}
            </Typography>
          ),
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            설정 파일은 저장하면 {data?.branch ?? '배포'} 브랜치에 PR 로 머지됩니다 · 현장 반영은 위 배포 버튼으로
          </Typography>
          <Button size="small" onClick={onRefresh} sx={{ textTransform: 'none', flexShrink: 0, minHeight: 36 }}>
            새로고침
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
